/**
 * CookieBanner — banner LGPD que aparece se o usuário ainda não consentiu.
 *
 * Persistência:
 *   localStorage.setItem("miller.cookie-consent.v1", "accepted")
 *
 * Posicionamento: bottom fixed, esconde quando aceito.
 *
 * Pra exibir novamente em testes:
 *   localStorage.removeItem("miller.cookie-consent.v1")
 *
 * Pra LGPD compliance, este banner cobre o caso de "cookies funcionais
 * essenciais" (auth, preferências, sessão). Não cobre cookies de tracking
 * de terceiros — se a Miller adicionar Analytics/Pixel/Ads no futuro,
 * tem que evoluir esse banner pra "Recusar tracking" granular.
 */
import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "miller.cookie-consent.v1";

export function CookieBanner() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (!consent) {
        // Pequeno delay pra não brigar com o page-load
        const timer = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage indisponível (SSR ou navegador restrito)
    }
  }, []);

  function accept() {
    try {
      localStorage.setItem(CONSENT_KEY, "accepted");
    } catch {}
    setShow(false);
  }

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 z-[300] sm:bottom-6 sm:left-6 sm:right-auto sm:max-w-md animate-modal-fade"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
    >
      <div className="bg-app-surface border border-app rounded-2xl shadow-xl p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.8 11.99c-.5 5.46-5.13 9.66-10.6 9.51C5.74 21.34 1.39 16.65 1.5 11.13 1.62 5.62 6.18 1.27 11.7 1.5c.32.01.51.36.36.64-1.04 1.99-.36 4.49 1.6 5.59 1.43.8 3.18.65 4.43-.39.27-.22.66-.05.65.3-.04 1.16.84 2.16 2.01 2.27.31.03.55.31.51.62-.07.5-.21.99-.46 1.46zM10 8h.01M9 14h.01M14 12h.01" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="cookie-title" className="text-sm font-semibold text-app-1 mb-1">
              {t("cookies.title", "Sobre cookies")}
            </h3>
            <p id="cookie-desc" className="text-xs text-app-3 leading-relaxed">
              {t("cookies.description", "Usamos cookies essenciais para autenticação, preferências de tema e idioma. Não usamos cookies de rastreamento de terceiros.")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 justify-between">
          <Link
            href="/privacidade"
            className="btn-link underline underline-offset-2"
          >
            {t("cookies.privacyLink", "Política de privacidade")}
          </Link>
          <button
            onClick={accept}
            className="btn-primary btn-sm"
          >
            {t("cookies.accept", "Entendi")}
          </button>
        </div>
      </div>
    </div>
  );
}
